import { iconNavDots } from '@bsds-lib-component/icons/icons';
import { BsdsIcon } from '@bsds-lib-component/react';
import React, { useEffect, useId, useRef, useState } from 'react';

import type { DashboardPaymentSummaryItem, DashboardPaymentSummaryTableProps } from '../../../types/vida/table/dashboardPaymentSummaryTableType';
import { generateDashboardPaymentSummaryPDF } from '../../../utils/dashboardPdfUtils';
import Pagination from '../../pagination/Pagination';
import Tooltip from '../../tooltip/Tooltip';
import TableMenuPortal from '../shared/TableMenuPortal';
import { TABLE_MENU_ITEM_BUTTON_CLASS } from '../shared/tableMenuUtils';

const getStatusBadgeClass = (status?: string) => {
  switch (status) {
    case 'Pago':
    case 'Em dia':
      return 'bg-green-100 text-green-800';
    case 'Pendente':
    case 'Atenção':
      return 'bg-yellow-100 text-yellow-800';
    case 'Vencido':
    case 'Atrasado':
      return 'bg-red-100 text-red-800';
    case 'Cancelado':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const DashboardPaymentSummaryTable: React.FC<DashboardPaymentSummaryTableProps> = ({
  items,
  viewType,
  formatCurrency,
  formatPercent,
  onRowClick,
  emptyMessage = 'Nenhum dado encontrado',
  onExport,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openItemMenuId, setOpenItemMenuId] = useState<string | number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const emptyStateRef = useRef<HTMLTableCellElement>(null);
  const itemMenuRefs = useRef<Map<string | number, HTMLDivElement>>(new Map());
  const itemMenuButtonRefs = useRef<Map<string | number, HTMLButtonElement>>(new Map());
  const itemExportButtonRefs = useRef<Map<string | number, HTMLButtonElement>>(new Map());
  const menuButtonId = useId();

  const getColumnTitle = () => {
    switch (viewType) {
      case 'corretora':
        return 'Corretora';
      case 'estipulante':
        return 'Estipulante';
      case 'apolice':
        return 'Apólice';
      default:
        return 'Nome';
    }
  };

  // Close menu when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Close header menu
      if (
        !menuRef.current?.contains(target)
        && !menuButtonRef.current?.contains(target)
      ) {
        setIsMenuOpen(false);
      }

      // Close item menus
      if (openItemMenuId !== null) {
        const itemMenuRef = itemMenuRefs.current.get(openItemMenuId);
        const itemMenuButtonRef = itemMenuButtonRefs.current.get(openItemMenuId);
        if (
          itemMenuRef
          && !itemMenuRef.contains(target)
          && !itemMenuButtonRef?.contains(target)
        ) {
          setOpenItemMenuId(null);
        }
      }
    };

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isMenuOpen) {
          setIsMenuOpen(false);
          menuButtonRef.current?.focus();
        }
        if (openItemMenuId !== null) {
          const itemMenuButtonRef = itemMenuButtonRefs.current.get(openItemMenuId);
          setOpenItemMenuId(null);
          itemMenuButtonRef?.focus();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape as unknown as EventListener);
    };
  }, [isMenuOpen, openItemMenuId]);

  useEffect(() => {
    if (items.length === 0) {
      emptyStateRef.current?.focus();
    }
  }, [items.length]);

  useEffect(() => {
    if (isMenuOpen) {
      exportButtonRef.current?.focus();
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (openItemMenuId !== null) {
      const itemExportButtonRef = itemExportButtonRefs.current.get(openItemMenuId);
      itemExportButtonRef?.focus();
    }
  }, [openItemMenuId]);

  const handleRowClick = (item: DashboardPaymentSummaryItem, _event: React.MouseEvent) => {
    onRowClick?.(item);
  };

  const handleExport = () => {
    onExport?.();
    setIsMenuOpen(false);
  };

  const handleItemMenuToggle = (itemId: string | number, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenItemMenuId(openItemMenuId === itemId ? null : itemId);
  };

  const handleItemMenuKeyDown = (itemId: string | number, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId(openItemMenuId === itemId ? null : itemId);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      if (openItemMenuId !== itemId) {
        setOpenItemMenuId(itemId);
      } else {
        const itemExportButtonRef = itemExportButtonRefs.current.get(itemId);
        itemExportButtonRef?.focus();
      }
    } else if (event.key === 'Escape' && openItemMenuId === itemId) {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId(null);
      const itemMenuButtonRef = itemMenuButtonRefs.current.get(itemId);
      itemMenuButtonRef?.focus();
    }
  };

  const handleExportPdf = (item: DashboardPaymentSummaryItem, event: React.MouseEvent) => {
    event.stopPropagation();
    generateDashboardPaymentSummaryPDF(item, viewType, formatCurrency, formatPercent);
    setOpenItemMenuId(null);
  };

  const handleExportPdfKeyDown = (item: DashboardPaymentSummaryItem, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      generateDashboardPaymentSummaryPDF(item, viewType, formatCurrency, formatPercent);
      setOpenItemMenuId(null);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId(null);
      const itemMenuButtonRef = itemMenuButtonRefs.current.get(item.id);
      itemMenuButtonRef?.focus();
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsMenuOpen(!isMenuOpen);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isMenuOpen) {
        setIsMenuOpen(true);
      } else {
        exportButtonRef.current?.focus();
      }
    } else if (event.key === 'Escape' && isMenuOpen) {
      setIsMenuOpen(false);
      menuButtonRef.current?.focus();
    }
  };

  const handleExportKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleExport();
    } else if (event.key === 'Escape') {
      setIsMenuOpen(false);
      menuButtonRef.current?.focus();
    }
  };

  const handleRowKeyDown = (item: DashboardPaymentSummaryItem, event: React.KeyboardEvent) => {
    if (onRowClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onRowClick(item);
    }
  };

  return (
    <>
    <div className="w-full min-h-[150px] overflow-x-auto custom-scrollbar rounded-md shadow-xs">
      <div>
        <table
          className="min-w-[800px] w-full table-fixed"
          role="table"
          aria-label="Resumo de pagamentos do dashboard"
          aria-rowcount={items.length}
          aria-colcount={8}
        >
          <thead>
            <tr>
              <th className="px-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16" scope="col">
                <Tooltip label="Menu de ações">
                  <button
                    type="button"
                    id={menuButtonId}
                    ref={menuButtonRef}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMenuOpen(!isMenuOpen);
                    }}
                    onKeyDown={handleMenuKeyDown}
                    className="hover:bg-gray-100 rounded transition-colors"
                    aria-label="Menu de ações"
                    aria-expanded={isMenuOpen}
                    aria-haspopup="menu"
                    aria-controls={`${menuButtonId}-menu`}
                    aria-pressed={isMenuOpen}
                  >
                    <BsdsIcon icon={iconNavDots} className="w-3 h-3 text-gray-600" aria-hidden="true" />
                  </button>
                </Tooltip>
                <TableMenuPortal
                  isOpen={isMenuOpen}
                  anchorRef={menuButtonRef}
                  menuRef={menuRef}
                  align="left"
                  id={`${menuButtonId}-menu`}
                  aria-labelledby={menuButtonId}
                  aria-label="Ações da tabela"
                  className="w-40"
                >
                  <button
                    ref={exportButtonRef}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExport();
                    }}
                    onKeyDown={handleExportKeyDown}
                    disabled={!onExport}
                    role="menuitem"
                    aria-disabled={!onExport}
                    className={`${TABLE_MENU_ITEM_BUTTON_CLASS} ${onExport
                      ? ''
                      : 'text-gray-400 cursor-not-allowed opacity-50'
                      }`}
                  >
                    Exportar em Excel
                  </button>
                </TableMenuPortal>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label={getColumnTitle()}>
                  <span>{getColumnTitle()}</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Percentual pendente">
                  <span>% Pendente</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Percentual emitido">
                  <span>% Emitido</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Valor pendente">
                  <span>Valor Pendente</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Valor emitido">
                  <span>Valor Emitido</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Status do item">
                  <span>Status</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16" scope="col">
                <Tooltip label="Ações">
                  <span>Ações</span>
                </Tooltip>
              </th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-50 text-text-color">
            {items && items.length > 0 ? (
              items.map((item: DashboardPaymentSummaryItem) => (
                <tr
                  key={item.id}
                  className={`hover:bg-gray-50 transition-colors text-xs ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={(e) => handleRowClick(item, e)}
                  onKeyDown={(e) => handleRowKeyDown(item, e)}
                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? "row" : undefined}
                >
                  <td className="px-2 whitespace-nowrap" />
                  <td className="px-4 py-2 whitespace-nowrap">{item.contractNumber}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{formatPercent(item.percentPending)}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{formatPercent(item.percentIssued)}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{formatCurrency(item.valuePending)}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{formatCurrency(item.valueIssued)}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-md text-xs font-semibold ${getStatusBadgeClass(item.status)}`}>
                      {item.status || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <Tooltip label="Ações do item">
                      <button
                        type="button"
                        ref={(el) => {
                          if (el) {
                            itemMenuButtonRefs.current.set(item.id, el);
                          } else {
                            itemMenuButtonRefs.current.delete(item.id);
                          }
                        }}
                        onClick={(e) => handleItemMenuToggle(item.id, e)}
                        onKeyDown={(e) => handleItemMenuKeyDown(item.id, e)}
                        className="hover:bg-gray-100 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-500 rounded transition-colors p-1"
                        aria-label={`Ações do item ${item.name}`}
                        aria-expanded={openItemMenuId === item.id}
                        aria-haspopup="menu"
                        aria-controls={`item-menu-${item.id}`}
                        aria-pressed={openItemMenuId === item.id}
                      >
                        <BsdsIcon icon={iconNavDots} className="w-3 h-3 text-gray-600" aria-hidden="true" />
                      </button>
                    </Tooltip>
                    <TableMenuPortal
                      isOpen={openItemMenuId === item.id}
                      anchorElement={itemMenuButtonRefs.current.get(item.id) ?? null}
                      menuRef={(el) => {
                        if (el) {
                          itemMenuRefs.current.set(item.id, el);
                        } else {
                          itemMenuRefs.current.delete(item.id);
                        }
                      }}
                      align="right"
                      id={`item-menu-${item.id}`}
                      className="w-40"
                      aria-label={`Menu de ações do item ${item.name}`}
                    >
                      <button
                        type="button"
                        ref={(el) => {
                          if (el) {
                            itemExportButtonRefs.current.set(item.id, el);
                          } else {
                            itemExportButtonRefs.current.delete(item.id);
                          }
                        }}
                        onClick={(e) => handleExportPdf(item, e)}
                        onKeyDown={(e) => handleExportPdfKeyDown(item, e)}
                        role="menuitem"
                        className={TABLE_MENU_ITEM_BUTTON_CLASS}
                      >
                        Exportar em PDF
                      </button>
                    </TableMenuPortal>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="px-4 py-4 text-center text-text-color text-xs" role="status" aria-live="polite" ref={emptyStateRef} tabIndex={-1}>
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>

    {onPageChange != null
      && currentPage != null
      && totalPages != null
      && totalItems != null
      && totalItems > 0 && (
      <div className="bsds-mt-16 bsds-mb-16">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          totalItems={totalItems}
          itemLabel="Itens"
          alwaysShow
        />
      </div>
    )}
    </>
  );
};

export default DashboardPaymentSummaryTable;

